import { useEffect, useRef } from 'react';
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type {
  ChatConnection,
  ChatPage,
  HomePageData,
  ImportantDate,
  ImportantDateInput,
  Invite,
  InviteInput,
  LeagueInfo,
  NewsPost,
  NewsPostInput,
  Permission,
  Poll,
  PollInput,
  Rule,
  RuleInput,
  RuleSuggestion,
  RuleSuggestionStatus,
  SessionInfo,
  StandingsPageData,
  StatsJobStatus,
  StatsPageData,
  StatsRefreshStatus,
} from '@gl/shared';

import { api } from './client.js';

export const queryKeys = {
  session: ['session'] as const,
  league: ['league'] as const,
  home: ['home'] as const,
  news: ['news'] as const,
  newsPost: (slug: string) => ['news', slug] as const,
  importantDates: (includePast: boolean) => ['important-dates', includePast] as const,
  polls: (scope: string) => ['polls', scope] as const,
  rules: ['rules'] as const,
  standings: (season: string) => ['standings', season] as const,
  stats: (season: string) => ['stats', season] as const,
  ruleSuggestions: ['rule-suggestions'] as const,
  chats: (season: string, search: string) => ['chats', season, search] as const,
  statsRefresh: ['stats-refresh'] as const,
  adminUsers: ['admin', 'users'] as const,
  adminInvites: ['admin', 'invites'] as const,
  sleeperUsers: ['admin', 'sleeper-users'] as const,
  statsJob: ['admin', 'stats-job'] as const,
  chatConnection: ['admin', 'chat'] as const,
};

/* -------------------- session -------------------- */

export function useSession() {
  return useQuery({
    queryKey: queryKeys.session,
    queryFn: () => api.get<SessionInfo>('/api/me'),
    // The signed-in user rarely changes mid-visit and gates most of the UI.
    staleTime: 5 * 60 * 1000,
  });
}

export function useLogout() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<void>('/api/auth/logout'),
    onSuccess: () => client.clear(),
  });
}

/* -------------------- league -------------------- */

/**
 * Where each season of the league lives on Sleeper. Two columns that only
 * change when a new season is created, so it is fetched once and kept.
 */
export function useLeague() {
  return useQuery({
    queryKey: queryKeys.league,
    queryFn: () => api.get<LeagueInfo>('/api/league'),
    staleTime: Infinity,
  });
}

/** The Sleeper link for one season, or the newest season's when none is named. */
export function useLeagueLink(season?: string) {
  const { data } = useLeague();
  const seasons = data?.seasons ?? [];
  return (
    (season ? seasons.find((entry) => entry.season === season) : seasons[seasons.length - 1]) ?? null
  );
}

/* -------------------- home -------------------- */

export function useHome() {
  return useQuery({ queryKey: queryKeys.home, queryFn: () => api.get<HomePageData>('/api/home') });
}

/* -------------------- standings -------------------- */

/** One season's standings plus the overall table, which spans every season. */
export function useStandings(season: string) {
  return useQuery({
    queryKey: queryKeys.standings(season),
    queryFn: () =>
      api.get<StandingsPageData>(
        season ? `/api/standings?season=${encodeURIComponent(season)}` : '/api/standings',
      ),
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

/* -------------------- stats -------------------- */

/**
 * One season tab's worth of statistics. An empty `season` lets the server pick
 * the default, which is the most recent season it has ingested.
 */
export function useStats(season: string) {
  return useQuery({
    queryKey: queryKeys.stats(season),
    queryFn: () =>
      api.get<StatsPageData>(
        season ? `/api/stats?season=${encodeURIComponent(season)}` : '/api/stats',
      ),
    // Statistics only change when the nightly job runs, and keeping the old tab
    // on screen while the next one loads stops the page jumping under a reader.
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

/**
 * Whether the statistics are being rebuilt, and when they last finished.
 *
 * Followed closely while a run is on and idly otherwise, and it refreshes the
 * pages it sits on the moment a run finishes — the point of showing "being
 * recalculated" is that the reader gets the new numbers without having to guess
 * when to reload.
 */
export function useStatsRefresh() {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: queryKeys.statsRefresh,
    queryFn: () => api.get<StatsRefreshStatus>('/api/stats/status'),
    refetchInterval: (current) => (current.state.data?.running ? 3000 : 60_000),
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  const running = query.data?.running ?? false;
  const wasRunning = useRef(running);

  useEffect(() => {
    if (wasRunning.current && !running) {
      void client.invalidateQueries({ queryKey: ['stats'] });
      void client.invalidateQueries({ queryKey: ['standings'] });
      void client.invalidateQueries({ queryKey: queryKeys.home });
    }
    wasRunning.current = running;
  }, [running, client]);

  return query;
}

/* -------------------- chats -------------------- */

/**
 * The league chat, a page at a time.
 *
 * Read-only, and paged backwards: the newest messages arrive first and asking
 * for more walks further into the history, which is the only direction an
 * archive grows in. The mirror only changes when the nightly job runs, so a
 * page already fetched is kept rather than re-requested.
 */
export function useChats(season: string, search: string, enabled = true) {
  return useInfiniteQuery({
    queryKey: queryKeys.chats(season, search),
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams();
      if (season) params.set('season', season);
      if (search) params.set('search', search);
      if (pageParam) params.set('before', pageParam);
      return api.get<ChatPage>(`/api/chats?${params.toString()}`);
    },
    initialPageParam: '',
    getNextPageParam: (last) => last.olderCursor ?? undefined,
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/* -------------------- news -------------------- */

export function useNews() {
  return useQuery({ queryKey: queryKeys.news, queryFn: () => api.get<NewsPost[]>('/api/news') });
}

export function useNewsPost(slug: string) {
  return useQuery({
    queryKey: queryKeys.newsPost(slug),
    queryFn: () => api.get<NewsPost>(`/api/news/${slug}`),
  });
}

export function useSaveNewsPost() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id?: string; input: NewsPostInput }) =>
      id
        ? api.patch<{ id: string; slug: string }>(`/api/news/${id}`, input)
        : api.post<{ id: string; slug: string }>('/api/news', input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.news });
      void client.invalidateQueries({ queryKey: queryKeys.home });
    },
  });
}

export function useDeleteNewsPost() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/news/${id}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.news });
      void client.invalidateQueries({ queryKey: queryKeys.home });
    },
  });
}

export function useUploadImage() {
  return useMutation({
    mutationFn: (file: File) =>
      api.upload<{ id: string; url: string; markdown: string }>('/api/uploads', file),
  });
}

/* -------------------- important dates -------------------- */

export function useImportantDates(includePast = false, enabled = true) {
  return useQuery({
    queryKey: queryKeys.importantDates(includePast),
    queryFn: () => api.get<ImportantDate[]>(`/api/important-dates?includePast=${includePast}`),
    enabled,
  });
}

export function useSaveImportantDate() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id?: string; input: ImportantDateInput }) =>
      id
        ? api.patch<ImportantDate>(`/api/important-dates/${id}`, input)
        : api.post<ImportantDate>('/api/important-dates', input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['important-dates'] });
      void client.invalidateQueries({ queryKey: queryKeys.home });
    },
  });
}

export function useDeleteImportantDate() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/important-dates/${id}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['important-dates'] });
      void client.invalidateQueries({ queryKey: queryKeys.home });
    },
  });
}

/* -------------------- polls -------------------- */

export function usePolls(scope: 'active' | 'past' | 'all', enabled = true) {
  return useQuery({
    queryKey: queryKeys.polls(scope),
    queryFn: () => api.get<Poll[]>(`/api/polls?scope=${scope}`),
    enabled,
  });
}

function invalidatePolls(client: ReturnType<typeof useQueryClient>) {
  void client.invalidateQueries({ queryKey: ['polls'] });
  void client.invalidateQueries({ queryKey: queryKeys.home });
}

export function useVote() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ pollId, optionIds }: { pollId: string; optionIds: string[] }) =>
      api.put<Poll>(`/api/polls/${pollId}/vote`, { optionIds }),
    onSuccess: () => invalidatePolls(client),
  });
}

/**
 * Creating takes a whole poll; editing takes only what changed — and leaving the
 * options out of an edit is the difference between rewording a question and
 * throwing away every vote cast on it.
 */
type SavePoll = { id?: undefined; input: PollInput } | { id: string; input: Partial<PollInput> };

export function useSavePoll() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: SavePoll) =>
      id ? api.patch<Poll>(`/api/polls/${id}`, input) : api.post<Poll>('/api/polls', input),
    onSuccess: () => invalidatePolls(client),
  });
}

export function useEndPoll() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reopen }: { id: string; reopen: boolean }) =>
      api.post<Poll>(`/api/polls/${id}/end`, { reopen }),
    onSuccess: () => invalidatePolls(client),
  });
}

export function useDeletePoll() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/polls/${id}`),
    onSuccess: () => invalidatePolls(client),
  });
}

/* -------------------- rules -------------------- */

export function useRules() {
  return useQuery({ queryKey: queryKeys.rules, queryFn: () => api.get<Rule[]>('/api/rules') });
}

export function useRuleSuggestions() {
  return useQuery({
    queryKey: queryKeys.ruleSuggestions,
    queryFn: () => api.get<RuleSuggestion[]>('/api/rules/suggestions/all'),
  });
}

function invalidateSuggestions(client: ReturnType<typeof useQueryClient>) {
  void client.invalidateQueries({ queryKey: queryKeys.ruleSuggestions });
}

export function useSaveSuggestion() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, description }: { id?: string; description: string }) =>
      id
        ? api.patch<RuleSuggestion>(`/api/rules/suggestions/${id}`, { description })
        : api.post<RuleSuggestion>('/api/rules/suggestions', { description }),
    onSuccess: () => invalidateSuggestions(client),
  });
}

export function useSetSuggestionStatus() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: RuleSuggestionStatus }) =>
      api.post<RuleSuggestion>(`/api/rules/suggestions/${id}/status`, { status }),
    onSuccess: () => invalidateSuggestions(client),
  });
}

export function useDeleteSuggestion() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/rules/suggestions/${id}`),
    onSuccess: () => invalidateSuggestions(client),
  });
}

export function useAddSuggestionComment() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: string }) =>
      api.post<RuleSuggestion>(`/api/rules/suggestions/${id}/comments`, { body }),
    onSuccess: () => invalidateSuggestions(client),
  });
}

export function useDeleteSuggestionComment() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, commentId }: { id: string; commentId: string }) =>
      api.delete<void>(`/api/rules/suggestions/${id}/comments/${commentId}`),
    onSuccess: () => invalidateSuggestions(client),
  });
}

export function useSaveRule() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id?: string; input: Partial<RuleInput> }) =>
      id ? api.patch<Rule>(`/api/rules/${id}`, input) : api.post<Rule>('/api/rules', input),
    onSuccess: () => void client.invalidateQueries({ queryKey: queryKeys.rules }),
  });
}

export function useDeleteRule() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/rules/${id}`),
    onSuccess: () => void client.invalidateQueries({ queryKey: queryKeys.rules }),
  });
}

/* -------------------- the league chat connection -------------------- */

/**
 * Signing in to Sleeper from the reader's own browser.
 *
 * This is the one request the site makes to somebody else's API from the
 * client, and it is deliberate. Sleeper answers a sign-in made by a server with
 * a captcha — a server cannot prove it is human, and should not be made to look
 * as though it can — so the sign-in happens here instead, on an admin's own
 * machine, and only the session token it returns is sent to our server. The
 * password never leaves the browser.
 */
export class SleeperCaptchaRequired extends Error {
  constructor(readonly said: string) {
    super(said);
    this.name = 'SleeperCaptchaRequired';
  }
}

async function signInToSleeper(
  login: string,
  password: string,
  captcha: string | null,
): Promise<string> {
  const response = await fetch('https://sleeper.com/graphql', {
    method: 'POST',
    headers: { 'content-type': 'application/json', 'x-sleeper-graphql-op': 'login' },
    body: JSON.stringify({
      operationName: 'login',
      variables: { login, password, captcha },
      query: `query login($login: String!, $password: String!, $captcha: String) {
        login(email_or_phone_or_username: $login, password: $password, captcha: $captcha) {
          token
          display_name
        }
      }`,
    }),
  });

  const body = (await response.json().catch(() => ({}))) as {
    data?: { login?: { token?: string } | null };
    errors?: Array<{ message?: string; code?: string }>;
  };

  const token = body.data?.login?.token;
  if (token) return token;

  const failure = body.errors?.[0];
  if (failure?.code?.includes('captcha') || /captcha/i.test(failure?.message ?? '')) {
    // Thrown as its own type so the page can put the challenge on screen rather
    // than only telling the reader about it.
    throw new SleeperCaptchaRequired(failure?.message ?? 'Sleeper wants to check you are human.');
  }
  throw new Error(
    failure?.message ??
      'Sleeper would not accept that sign-in. Check the username and password and try again.',
  );
}

export function useChatConnection(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.chatConnection,
    queryFn: () => api.get<ChatConnection>('/api/admin/chat'),
    enabled,
  });
}

/**
 * Sign in over there, hand the session to us. Two steps, one button: the sign-in
 * has to happen in the browser, and the storing has to happen on the server.
 */
export function useConnectChat() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      login,
      password,
      captcha,
    }: {
      login: string;
      password: string;
      /** An hCaptcha answer, when Sleeper has asked for one. */
      captcha?: string | null;
    }) => {
      const token = await signInToSleeper(login, password, captcha ?? null);
      return api.put<ChatConnection>('/api/admin/chat/token', { token });
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: queryKeys.chatConnection }),
  });
}

export function useDisconnectChat() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<ChatConnection>('/api/admin/chat/token'),
    onSuccess: () => void client.invalidateQueries({ queryKey: queryKeys.chatConnection }),
  });
}

/* -------------------- admin -------------------- */

export interface AdminUser {
  id: string;
  email: string;
  displayName: string;
  avatarUrl: string | null;
  sleeperUserId: string | null;
  disabled: boolean;
  lastLoginAt: string | null;
  permissions: Permission[];
}

export interface SleeperUserRow {
  season: string;
  sleeperUserId: string;
  displayName: string;
  teamName: string | null;
  avatar: string | null;
}

export function useAdminUsers(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.adminUsers,
    queryFn: () => api.get<AdminUser[]>('/api/admin/users'),
    enabled,
  });
}

export function useInvites(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.adminInvites,
    queryFn: () => api.get<Invite[]>('/api/admin/invites'),
    enabled,
  });
}

export function useSleeperUsers(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.sleeperUsers,
    queryFn: () => api.get<SleeperUserRow[]>('/api/admin/sleeper-users'),
    enabled,
  });
}

export function useCreateInvite() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: InviteInput) => api.post<Invite>('/api/admin/invites', input),
    onSuccess: () => void client.invalidateQueries({ queryKey: queryKeys.adminInvites }),
  });
}

export function useDeleteInvite() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/admin/invites/${id}`),
    onSuccess: () => void client.invalidateQueries({ queryKey: queryKeys.adminInvites }),
  });
}

/**
 * Polls while a recalculation is running so the page follows it live, and
 * settles back to a single fetch once it finishes.
 */
export function useStatsJob(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.statsJob,
    queryFn: () => api.get<StatsJobStatus>('/api/admin/stats/status'),
    enabled,
    refetchInterval: (query) => (query.state.data?.running ? 2000 : false),
  });
}

export function useRecalculateStats() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (full: boolean) =>
      api.post<{ status: string }>('/api/admin/stats/recalculate', { full }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.statsJob });
      // The tables it rebuilds are cached under their own keys.
      void client.invalidateQueries({ queryKey: ['stats'] });
      void client.invalidateQueries({ queryKey: ['standings'] });
      void client.invalidateQueries({ queryKey: queryKeys.home });
    },
  });
}

/**
 * Re-points an account at a different Sleeper member. The session carries the
 * mapping too, so a change to your own account has to refresh it — that is what
 * every "your team" highlight on the site reads.
 */
export function useSetUserSleeperId() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, sleeperUserId }: { id: string; sleeperUserId: string | null }) =>
      api.put<{ id: string; sleeperUserId: string | null }>(
        `/api/admin/users/${id}/sleeper-user`,
        { sleeperUserId },
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.adminUsers });
      void client.invalidateQueries({ queryKey: queryKeys.session });
    },
  });
}

export function useSetUserPermissions() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, permissions }: { id: string; permissions: Permission[] }) =>
      api.put<{ id: string }>(`/api/admin/users/${id}/permissions`, { permissions }),
    onSuccess: () => void client.invalidateQueries({ queryKey: queryKeys.adminUsers }),
  });
}
